import { Duration } from "date-fns";
import {
  Boolean,
  Literal,
  Optional,
  Record as RRecord,
  Static,
  Union,
} from "runtypes";

import { ApiClientType } from "./enums.js";
import { getNormalEnum } from "./runtypeEnums";

// Each value is the number of days until a token expires
export const ApiTokenExpirationPolicyLiteral = Union(
  Literal("DAYS_7"),
  Literal("DAYS_30"),
  Literal("DAYS_60"),
  Literal("DAYS_90"),
  Literal("DAYS_120"),
  Literal("NO_EXPIRY"),
);

export type ApiTokenExpirationPolicy = Static<
  typeof ApiTokenExpirationPolicyLiteral
>;

export const ApiTokenExpirationPolicy = getNormalEnum(
  ApiTokenExpirationPolicyLiteral,
);

export const API_TOKEN_EXPIRATION_POLICY_ORDERING = [
  ApiTokenExpirationPolicy.DAYS_7,
  ApiTokenExpirationPolicy.DAYS_30,
  ApiTokenExpirationPolicy.DAYS_60,
  ApiTokenExpirationPolicy.DAYS_90,
  ApiTokenExpirationPolicy.DAYS_120,
  ApiTokenExpirationPolicy.NO_EXPIRY,
];

/**
 * Prefixes for API tokens to enable security scanning tools
 * (like GitHub Secret Scanning, Semgrep Secrets, etc.) to identify
 * Hex tokens that may have been accidentally exposed in source code.
 *
 * See: https://github.blog/engineering/platform-security/behind-githubs-new-authentication-token-formats/
 *
 * When adding a new external-facing token type, add a unique prefix here.
 */
export const API_TOKEN_PREFIX = {
  /** Internal/system tokens (app sessions, screenshots, table previews, etc.) */
  INTERNAL: "hxt_",
  /** Personal API tokens created by individual users */
  PERSONAL: "hxtp_",
  /** Workspace-level API tokens created by admins */
  WORKSPACE: "hxtw_",
  /** Bot token tokens for MCP integrations */
  MCP: "hxtb_",
} as const;

/**
 * Returns a safe display string for an API token, showing the prefix and last 4 characters.
 * Example: "hxtp_•••• ab12" for a personal token ending in "ab12"
 *
 * @param apiClientType - The type of API client (USER or WORKSPACE_TOKEN)
 * @param lastFour - The last 4 characters of the token, or null if not available
 * @returns A formatted string like "hxtp_•••• ab12", or null if lastFour is not available
 */
export function getDisplaySafeToken(
  apiClientType: ApiClientType.USER | ApiClientType.WORKSPACE_TOKEN,
  lastFour: string | null,
): string | null {
  if (!lastFour || lastFour.length !== 4) {
    return null;
  }

  const prefix =
    apiClientType === ApiClientType.WORKSPACE_TOKEN
      ? API_TOKEN_PREFIX.WORKSPACE
      : API_TOKEN_PREFIX.PERSONAL;

  return `${prefix}•••• ${lastFour}`;
}

export const API_TOKEN_EXPIRATION_POLICY_OFFSET: Record<
  ApiTokenExpirationPolicy,
  Duration | "NO_EXPIRY"
> = {
  [ApiTokenExpirationPolicy.DAYS_7]: { days: 7 },
  [ApiTokenExpirationPolicy.DAYS_30]: { days: 30 },
  [ApiTokenExpirationPolicy.DAYS_60]: { days: 60 },
  [ApiTokenExpirationPolicy.DAYS_90]: { days: 90 },
  [ApiTokenExpirationPolicy.DAYS_120]: { days: 120 },
  [ApiTokenExpirationPolicy.NO_EXPIRY]: "NO_EXPIRY",
};

export const ApiScope = RRecord({
  projectRead: Boolean,
  projectRun: Boolean,
  semanticLayerSync: Boolean,
});
export type ApiScope = Static<typeof ApiScope>;

export const EntityApiScope = RRecord({
  read: Boolean,
  write: Boolean,
}).withConstraint((scope) => !scope.write || scope.read);

export type EntityApiScope = Static<typeof EntityApiScope>;

export const ApiScopeV2 = RRecord({
  projectWrite: Optional(Boolean.nullable()),
  projectRead: Boolean,
  projectRun: Boolean,
  semanticLayerSync: Boolean,
  createEmbeddedLinks: Boolean,
  projectQueriedTables: Optional(Boolean.nullable()),

  dataConnectionsScope: Optional(EntityApiScope.nullable()),
  groupsScope: Optional(EntityApiScope.nullable()),
  usersScope: Optional(EntityApiScope.nullable()),
  collectionsScope: Optional(EntityApiScope.nullable()),
  cellsScope: Optional(EntityApiScope.nullable()),
}).withConstraint((scope) => !scope.projectWrite || scope.projectRead);
export type ApiScopeV2 = Static<typeof ApiScopeV2>;

const EmbeddedUserIdentifier = RRecord({ type: Literal("embedded_user") });
export const BaseEmbeddedUserScope = RRecord({
  createSavedView: Boolean,
  exportPdf: Boolean,
  exportCsv: Boolean,
  createScheduledRun: Boolean,
});
export type BaseEmbeddedUserScope = Static<typeof BaseEmbeddedUserScope>;
// Partial scopes are stored in the API table which are then cast into non partial types on read
export const PartialEmbeddedUserScope = EmbeddedUserIdentifier.And(
  BaseEmbeddedUserScope.asPartial(),
);
export type PartialEmbeddedUserScope = Static<typeof PartialEmbeddedUserScope>;
export const EmbeddedUserScope = EmbeddedUserIdentifier.And(
  BaseEmbeddedUserScope,
);
export type EmbeddedUserScope = Static<typeof EmbeddedUserScope>;
const EmbeddedUserScopeDefaults: EmbeddedUserScope = {
  type: "embedded_user",
  createSavedView: false,
  exportPdf: false,
  exportCsv: false,
  createScheduledRun: false,
};

export function convertApiScopeV1ToV2(scope: ApiScope): ApiScopeV2 {
  return {
    ...scope,
    createEmbeddedLinks: false,
  };
}

// Scopes that can be defined on the APIClient table
export type ApiClientScopes = ApiScope | ApiScopeV2 | PartialEmbeddedUserScope;

/**
 * Returns an ApiScopeV2 from the ApiClient.apiScope column. Used for API tokens
 */
export function getApiScopeV2(
  apiScope: ApiClientScopes,
  _apiClientType: ApiClientType.WORKSPACE_TOKEN, // We're just using this for the type checking
): ApiScopeV2 | null {
  // ApiScopeV2 does not have a type key that we can use to infer typing, so we have to check that is isn't any of the other variants
  if (PartialEmbeddedUserScope.guard(apiScope)) {
    return null;
  }
  return ApiScopeV2.guard(apiScope)
    ? apiScope
    : convertApiScopeV1ToV2(apiScope);
}

/**
 * Returns an EmbeddedUser scope from the ApiClient.apiScope column. Used for signed embedded users
 */
export function getEmbeddedUserScope(
  apiScope: ApiClientScopes,
  _apiClientType: ApiClientType.EMBEDDED_USER,
): EmbeddedUserScope | null {
  if (!PartialEmbeddedUserScope.guard(apiScope)) {
    return null;
  }

  return { ...EmbeddedUserScopeDefaults, ...apiScope };
}
